<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  PanelLeftOpenIcon,
  Search01Icon,
  UserSquareIcon,
} from '@hugeicons/core-free-icons';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import DashboardStatsAPI from 'dashboard/api/dashboardStats';

const props = defineProps({
  totalItems: { type: Number, default: 0 },
  hasAppliedFilters: { type: Boolean, default: false },
  activeSidebarFilter: { type: String, default: '' },
});

const emit = defineEmits(['collapse', 'applyFilter', 'selectAll']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { accountScopedRoute } = useAccount();

const segments = useMapGetter('customViews/getContactCustomViews');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const aiAgents = useMapGetter('aiAgents/getAgents');
const teams = useMapGetter('teams/getTeams');
const lifecycleStages = useMapGetter('lifecycleStages/getLifecycleStages');
const lostStages = useMapGetter('lifecycleStages/getLostStages');

const stats = ref(null);
const search = ref('');
const showSearch = ref(false);
const agentsOpen = ref(true);
const lifecycleOpen = ref(true);
const teamsOpen = ref(true);
const segmentsOpen = ref(true);

const segmentTo = id =>
  accountScopedRoute(
    'contacts_dashboard_segments_index',
    { segmentId: id },
    { page: 1 }
  );

const labelTo = title =>
  accountScopedRoute(
    'contacts_dashboard_labels_index',
    { label: title },
    { page: 1, search: undefined }
  );

const isAllActive = computed(() => {
  if (props.hasAppliedFilters) return false;
  if (route.name === 'contacts_dashboard_index') return true;
  if (route.name === 'contacts_edit') return true;
  return false;
});

const activeSegmentId = computed(() =>
  route.params.segmentId ? Number(route.params.segmentId) : null
);

const isSegmentNavActive = viewId =>
  (route.name === 'contacts_dashboard_segments_index' ||
    route.name === 'contacts_edit_segment') &&
  activeSegmentId.value === Number(viewId);

const activeLabelTitle = computed(() => route.params.label || null);

const isLabelNavActive = title =>
  (route.name === 'contacts_dashboard_labels_index' ||
    route.name === 'contacts_edit_label') &&
  activeLabelTitle.value === title;

const normalize = value => String(value || '').toLowerCase();
const includesSearch = value =>
  normalize(value).includes(normalize(search.value));

const aiAgentItems = computed(() =>
  aiAgents.value.filter(agent => agent.status === 'published')
);
const lifecycleItems = computed(
  () =>
    stats.value?.lifecycle_stages?.filter(
      stage => stage.category === 'lifecycle_stage'
    ) || lifecycleStages.value
);
const lostStageItems = computed(
  () =>
    stats.value?.lifecycle_stages?.filter(
      stage => stage.category === 'lost_stage'
    ) || lostStages.value
);
const teamItems = computed(() => stats.value?.teams || teams.value);
const allContactsCount = computed(
  () => stats.value?.contacts_count ?? props.totalItems
);

const filteredAiAgents = computed(() =>
  aiAgentItems.value.filter(agent => includesSearch(agent.name))
);

const filteredLifecycleStages = computed(() =>
  lifecycleItems.value.filter(stage => includesSearch(stage.name))
);

const filteredLostStages = computed(() =>
  lostStageItems.value.filter(stage => includesSearch(stage.name))
);

const filteredTeams = computed(() =>
  teamItems.value.filter(team => includesSearch(team.name))
);

const filteredSegments = computed(() =>
  segments.value.filter(view => includesSearch(view.name))
);

const filteredLabels = computed(() =>
  labels.value.filter(label => includesSearch(label.title))
);

const rowClass = active =>
  [
    'mb-1 flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-2 text-start text-sm transition-colors',
    active
      ? 'bg-n-alpha-2 font-semibold text-n-slate-12'
      : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
  ].join(' ');

const sectionBtnClass = open =>
  [
    'mb-1 flex h-8 w-full items-center justify-between gap-2 px-2 text-start text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12',
    open ? 'text-n-slate-12' : '',
  ].join(' ');

const countClass = active =>
  [
    'shrink-0 text-xs tabular-nums',
    active ? 'font-semibold text-n-slate-12' : 'text-n-slate-10',
  ].join(' ');

const filterKey = (attributeKey, value) => `${attributeKey}:${value}`;

const isDirectFilterActive = (attributeKey, value) =>
  route.name === 'contacts_dashboard_index' &&
  props.activeSidebarFilter === filterKey(attributeKey, value);

const filterFor = (attributeKey, value) => [
  {
    attributeKey,
    filterOperator: 'equal_to',
    values: [String(value)],
    queryOperator: 'and',
    attributeModel: 'standard',
  },
];

const applyDirectFilter = (attributeKey, value) => {
  emit('applyFilter', filterFor(attributeKey, value));
};

const aiAgentContactCount = agent =>
  stats.value?.assigned_agent_bot_counts?.[agent.agent_bot_id] ?? 0;
const teamContactCount = team => team.contacts_count ?? team.contactsCount ?? 0;
const stageContactCount = stage =>
  stage.contacts_count ?? stage.contactsCount ?? 0;
const labelContactCount = label =>
  label.contacts_count ?? label.contactsCount ?? 0;
const segmentContactCount = view =>
  view.contacts_count ?? view.contactsCount ?? 0;

const closeSearch = () => {
  search.value = '';
  showSearch.value = false;
};

const fetchStats = async () => {
  try {
    const response = await DashboardStatsAPI.get();
    stats.value = response.data;
  } catch {
    stats.value = null;
  }
};

onMounted(async () => {
  store.dispatch('aiAgents/get');
  store.dispatch('teams/get');
  store.dispatch('lifecycleStages/get');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('labels/get');
  await fetchStats();
});
</script>

<template>
  <aside
    class="flex h-full min-h-0 w-full shrink-0 flex-col border-e border-n-weak bg-n-surface-1 dark:border-n-strong"
    :aria-label="t('CONTACTS_LAYOUT.SIDEBAR_NAV.ARIA')"
  >
    <div v-if="showSearch" class="flex h-12 shrink-0 items-center gap-1 px-2">
      <div
        class="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-n-weak bg-n-alpha-1 px-3 transition-colors focus-within:border-n-strong"
      >
        <HugeIcon
          :icon="Search01Icon"
          :size="18"
          class="shrink-0 text-n-slate-12"
        />
        <input
          v-model="search"
          type="text"
          :placeholder="t('CONTACTS_LAYOUT.SIDEBAR_NAV.SEARCH')"
          class="reset-base h-5 min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm leading-5 text-n-slate-12 shadow-none outline-none ring-0 placeholder:text-n-slate-10 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none"
          autofocus
        />
      </div>
      <button
        class="flex size-8 items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
        type="button"
        :aria-label="t('CONTACTS_LAYOUT.SIDEBAR_NAV.CLOSE_SEARCH')"
        @click="closeSearch"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>
    <div v-else class="flex h-12 shrink-0 items-center gap-2 px-3">
      <h2
        class="min-w-0 flex-1 truncate text-base font-semibold text-n-slate-12"
      >
        {{ t('SIDEBAR.CONTACTS') }}
      </h2>
      <button
        class="flex size-7 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        type="button"
        :aria-label="t('CONTACTS_LAYOUT.SIDEBAR_NAV.SEARCH')"
        @click="showSearch = true"
      >
        <HugeIcon :icon="Search01Icon" :size="18" class="shrink-0" />
      </button>
      <button
        class="flex size-7 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        type="button"
        :aria-label="t('CONTACTS_LAYOUT.SIDEBAR_NAV.COLLAPSE')"
        @click="emit('collapse')"
      >
        <HugeIcon :icon="PanelLeftOpenIcon" :size="18" class="shrink-0" />
      </button>
    </div>

    <nav class="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-4">
      <button
        type="button"
        :class="rowClass(isAllActive)"
        :aria-current="isAllActive ? 'page' : undefined"
        @click="emit('selectAll')"
      >
        <HugeIcon :icon="UserSquareIcon" :size="18" class="shrink-0" />
        <span class="min-w-0 flex-1 truncate text-start">
          {{ t('SIDEBAR.ALL_CONTACTS') }}
        </span>
        <span v-if="allContactsCount" :class="countClass(isAllActive)">
          {{ allContactsCount }}
        </span>
      </button>
      <div class="my-2 border-t border-n-weak" />

      <div>
        <button
          type="button"
          :class="sectionBtnClass(teamsOpen)"
          :aria-expanded="teamsOpen"
          @click="teamsOpen = !teamsOpen"
        >
          <span class="truncate">{{
            t('CONTACTS_LAYOUT.SIDEBAR_NAV.TEAMS')
          }}</span>
          <HugeIcon
            :icon="teamsOpen ? ArrowUp01Icon : ArrowDown01Icon"
            :size="18"
            class="shrink-0"
          />
        </button>
        <div v-show="teamsOpen">
          <button
            v-for="team in filteredTeams"
            :key="`team-${team.id}`"
            type="button"
            :class="rowClass(isDirectFilterActive('contact_team_id', team.id))"
            @click="applyDirectFilter('contact_team_id', team.id)"
          >
            <span class="min-w-0 flex-1 truncate text-start">
              {{ team.name }}
            </span>
            <span
              :class="
                countClass(isDirectFilterActive('contact_team_id', team.id))
              "
            >
              {{ teamContactCount(team) }}
            </span>
          </button>
        </div>
      </div>

      <div class="my-2 border-t border-n-weak" />

      <div>
        <button
          type="button"
          :class="sectionBtnClass(agentsOpen)"
          :aria-expanded="agentsOpen"
          @click="agentsOpen = !agentsOpen"
        >
          <span class="truncate">{{
            t('CONTACTS_LAYOUT.SIDEBAR_NAV.AI_AGENTS')
          }}</span>
          <HugeIcon
            :icon="agentsOpen ? ArrowUp01Icon : ArrowDown01Icon"
            :size="18"
            class="shrink-0"
          />
        </button>
        <div v-show="agentsOpen">
          <button
            v-for="agent in filteredAiAgents"
            :key="`agent-${agent.id}`"
            type="button"
            :class="
              rowClass(
                isDirectFilterActive(
                  'assignee_agent_bot_id',
                  agent.agent_bot_id
                )
              )
            "
            @click="
              applyDirectFilter('assignee_agent_bot_id', agent.agent_bot_id)
            "
          >
            <span
              class="inline-flex size-[18px] shrink-0 items-center justify-center text-[18px] leading-none"
            >
              {{ agent.emoji || agent.name?.charAt(0) }}
            </span>
            <span class="min-w-0 flex-1 truncate text-start">
              {{ agent.name }}
            </span>
            <span
              :class="
                countClass(
                  isDirectFilterActive(
                    'assignee_agent_bot_id',
                    agent.agent_bot_id
                  )
                )
              "
            >
              {{ aiAgentContactCount(agent) }}
            </span>
          </button>
        </div>
      </div>

      <div class="my-2 border-t border-n-weak" />

      <div>
        <button
          type="button"
          :class="sectionBtnClass(lifecycleOpen)"
          :aria-expanded="lifecycleOpen"
          @click="lifecycleOpen = !lifecycleOpen"
        >
          <span class="truncate">{{
            t('CONTACTS_LAYOUT.SIDEBAR_NAV.LIFECYCLE')
          }}</span>
          <HugeIcon
            :icon="lifecycleOpen ? ArrowUp01Icon : ArrowDown01Icon"
            :size="18"
            class="shrink-0"
          />
        </button>
        <div v-show="lifecycleOpen">
          <p
            v-if="filteredLifecycleStages.length"
            class="px-2 pb-1 pt-2 text-xs font-semibold text-n-slate-11"
          >
            {{ t('CONTACTS_LAYOUT.SIDEBAR_NAV.LIFECYCLE_STAGES') }}
          </p>
          <button
            v-for="stage in filteredLifecycleStages"
            :key="`life-${stage.id}`"
            type="button"
            :class="
              rowClass(isDirectFilterActive('lifecycle_stage_id', stage.id))
            "
            @click="applyDirectFilter('lifecycle_stage_id', stage.id)"
          >
            <span
              class="inline-flex size-[18px] shrink-0 items-center justify-center text-[18px] leading-none"
            >
              {{ stage.icon }}
            </span>
            <span class="min-w-0 flex-1 truncate text-start">
              {{ stage.name }}
            </span>
            <span
              :class="
                countClass(isDirectFilterActive('lifecycle_stage_id', stage.id))
              "
            >
              {{ stageContactCount(stage) }}
            </span>
          </button>

          <p
            v-if="filteredLostStages.length"
            class="px-2 pb-1 pt-2 text-xs font-semibold text-n-slate-11"
          >
            {{ t('CONTACTS_LAYOUT.SIDEBAR_NAV.LOST_STAGES') }}
          </p>
          <button
            v-for="stage in filteredLostStages"
            :key="`lost-${stage.id}`"
            type="button"
            :class="
              rowClass(isDirectFilterActive('lifecycle_stage_id', stage.id))
            "
            @click="applyDirectFilter('lifecycle_stage_id', stage.id)"
          >
            <span
              class="inline-flex size-[18px] shrink-0 items-center justify-center text-[18px] leading-none"
            >
              {{ stage.icon }}
            </span>
            <span class="min-w-0 flex-1 truncate text-start">
              {{ stage.name }}
            </span>
            <span
              :class="
                countClass(isDirectFilterActive('lifecycle_stage_id', stage.id))
              "
            >
              {{ stageContactCount(stage) }}
            </span>
          </button>
        </div>
      </div>

      <div class="my-2 border-t border-n-weak" />

      <div>
        <button
          type="button"
          :class="sectionBtnClass(segmentsOpen)"
          :aria-expanded="segmentsOpen"
          @click="segmentsOpen = !segmentsOpen"
        >
          <span class="truncate">{{ t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS') }}</span>
          <HugeIcon
            :icon="segmentsOpen ? ArrowUp01Icon : ArrowDown01Icon"
            :size="18"
            class="shrink-0"
          />
        </button>
        <div v-show="segmentsOpen">
          <RouterLink
            v-for="view in filteredSegments"
            :key="`seg-${view.id}`"
            :to="segmentTo(view.id)"
            :class="rowClass(isSegmentNavActive(view.id))"
            :aria-current="isSegmentNavActive(view.id) ? 'page' : undefined"
          >
            <span class="min-w-0 flex-1 truncate">{{ view.name }}</span>
            <span
              v-if="segmentContactCount(view)"
              :class="countClass(isSegmentNavActive(view.id))"
            >
              {{ segmentContactCount(view) }}
            </span>
          </RouterLink>

          <RouterLink
            v-for="label in filteredLabels"
            :key="`lbl-${label.id}`"
            :to="labelTo(label.title)"
            :class="rowClass(isLabelNavActive(label.title))"
            :aria-current="isLabelNavActive(label.title) ? 'page' : undefined"
          >
            <span class="min-w-0 flex-1 truncate">{{ label.title }}</span>
            <span
              v-if="labelContactCount(label)"
              :class="countClass(isLabelNavActive(label.title))"
            >
              {{ labelContactCount(label) }}
            </span>
          </RouterLink>
        </div>
      </div>

      <div class="my-2 border-t border-n-weak" />

      <button
        v-if="!search"
        type="button"
        :class="rowClass(isDirectFilterActive('blocked', 'true'))"
        @click="applyDirectFilter('blocked', 'true')"
      >
        <span class="min-w-0 flex-1 truncate text-start">
          {{ t('CONTACTS_LAYOUT.SIDEBAR_NAV.BLOCKED_CONTACTS') }}
        </span>
      </button>
    </nav>
  </aside>
</template>
